import { useState } from "react";
import { Modal } from "@mui/material";
import Image from "next/image";
import { useSession } from "next-auth/react";
import CategoryContainer from "../categoryContainer";
import HoursContainer from "../hoursContainer";
import LanguageContainer from "../languageContainer";
import MobilityContainer from "../mobilityContainer";
import TagContainer from "../tagsContainer";
import { advancedFilters } from "@/api/traveler";
import { useTranslation } from "@/app/i18n/client";
import Close from "@/assets/images/icons/close.svg";
import CustomButton from "@/common/customElements/button";
import DistanceSlider from "@/common/customElements/distanceSlider";
import HexagonButton from "@/common/customElements/HexagonButton";
import RangeSlider from "@/common/customElements/priceSlider";
import { AbsoluteDiv, InlineDiv, Separator } from "@/common/divs";
import {
  AlmostBoldText,
  LightText,
  RegularText,
  SemiBoldText,
  BlackTextLink,
} from "@/common/texts";
import {
  CategoryType,
  LanguageType,
  ModalFiltersProps,
  TagType,
} from "@/types/components";
import {
  NEUTRO_TITLES_COLOR,
  PRIMARY_BUTTON_COLOR,
  PRIMARY_BUTTON_COLOR_DISSABLED,
} from "@/utils/constants";

const ModalFilters: React.FC<ModalFiltersProps> = ({
  open,
  setOpen,
  valuesState,
  categories,
  tags,
  parseTerm,
  changeValuesForFetch,
  mobilityTags,
  languages,
}) => {
  const { data: session } = useSession();
  const INITIAL_DISTANCE = 50000;
  const deleteAllFilters = () => {
    setDeleteAll(true);
    categories.current.forEach((category: CategoryType) => {
      category.selected = false;
    });
    tags.current.forEach((tag: CategoryType) => {
      tag.selected = false;
    });
    mobilityTags.current.forEach((tag: { id: number; selected: boolean }) => {
      tag.selected = false;
    });
    languages.current.forEach((language: { id: number; selected: boolean }) => {
      language.selected = false;
    });
    delete valuesState.current.term;
    delete valuesState.current.languages;
    delete valuesState.current.price;
    delete valuesState.current.hours;
    valuesState.current.distance = INITIAL_DISTANCE;
    delete valuesState.current.minPrice;
    delete valuesState.current.maxPrice;
    setTimeout(() => {
      setDeleteAll(false);
    }, 1000);
  };

  const [deleteAll, setDeleteAll] = useState<boolean>(false);
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className="centerModal filters-modal">
        <section className="modal-section-style">
          <AlmostBoldText fontSize="24px" style={{ marginTop: "0px" }}>
            {t("activities.filter.advanced_filters")}
          </AlmostBoldText>
          <AbsoluteDiv right="24px" top="24px">
            <HexagonButton
              onClick={() => setOpen(false)}
              width="32px"
              backgroundColor={PRIMARY_BUTTON_COLOR}
            >
              <Image
                alt="close"
                width="16"
                height="16"
                src={Close.src}
                style={{
                  position: "absolute",
                  top: "20%",
                  left: "27%",
                }}
              />
            </HexagonButton>
          </AbsoluteDiv>
          <Separator
            style={{ background: PRIMARY_BUTTON_COLOR_DISSABLED, opacity: "1" }}
          />
        </section>
        <section className="mini-modal-section">
          <SemiBoldText fontSize="16px" style={{ marginTop: "20px" }}>
            {t("activities.filter.category")}
          </SemiBoldText>
          <InlineDiv className="inline-modal-div">
            {categories.current.map((category: CategoryType) => (
              <CategoryContainer
                category={category}
                key={category.id}
                categories={categories}
                parseTerm={parseTerm}
                fromCheckbox={true}
                deleteAll={deleteAll}
              />
            ))}
          </InlineDiv>
          <Separator
            style={{ background: PRIMARY_BUTTON_COLOR_DISSABLED, opacity: "1" }}
          />
        </section>
        <section className="mini-modal-section">
          <SemiBoldText fontSize="16px" style={{ marginTop: "20px" }}>
            {t("activities.filter.tags")}
          </SemiBoldText>
          <InlineDiv className="inline-modal-div">
            {tags.current.map((tag: CategoryType) => (
              <TagContainer
                key={tag.id}
                tag={tag}
                tags={tags}
                parseTerm={parseTerm}
                deleteAll={deleteAll}
              />
            ))}
          </InlineDiv>
          <Separator
            style={{ background: PRIMARY_BUTTON_COLOR_DISSABLED, opacity: "1" }}
          />
        </section>
        <section className="mini-modal-section">
          <InlineDiv className="direction-flex-container">
            <div className="auto-width">
              <SemiBoldText
                fontSize="16px"
                style={{ marginTop: "20px", marginBottom: "16px" }}
              >
                {t("activities.filter.price")}
              </SemiBoldText>
              <RangeSlider valuesState={valuesState} deleteAll={deleteAll} />
            </div>
            <div className="auto-width">
              <InlineDiv className="space-modal-div">
                <SemiBoldText fontSize="16px">
                  {t("activity.map.activity_duration")}
                </SemiBoldText>
                <LightText>{t("activity.map.activity_hours")}</LightText>
              </InlineDiv>
              <InlineDiv style={{ gap: "10px" }}>
                <HoursContainer
                  valuesState={valuesState}
                  deleteAll={deleteAll}
                />
              </InlineDiv>
            </div>
          </InlineDiv>
          <Separator
            style={{ background: PRIMARY_BUTTON_COLOR_DISSABLED, opacity: "1" }}
          />
        </section>
        <section className="mini-modal-section">
          <InlineDiv className="direction-flex-container">
            <div className="auto-width">
              <SemiBoldText
                fontSize="16px"
                style={{ marginTop: "20px", marginBottom: "16px" }}
              >
                {t("activity.mobility")}
              </SemiBoldText>

              <InlineDiv
                className="inline-modal-div"
                style={{
                  minWidth: "225px",
                }}
              >
                {mobilityTags.current.map((tag: TagType) => (
                  <MobilityContainer
                    key={tag.id}
                    tag={tag}
                    tags={mobilityTags}
                    parseTerm={parseTerm}
                    deleteAll={deleteAll}
                  />
                ))}
              </InlineDiv>
            </div>
            <div className="auto-width">
              <SemiBoldText
                fontSize="16px"
                style={{ marginTop: "20px", marginBottom: "50px" }}
              >
                {t("activities.filter.distance_placeholder")} (km)
              </SemiBoldText>
              <DistanceSlider valuesState={valuesState} deleteAll={deleteAll} />
            </div>
          </InlineDiv>
          <Separator
            style={{ background: PRIMARY_BUTTON_COLOR_DISSABLED, opacity: "1" }}
          />
        </section>
        <section className="mini-modal-section">
          <SemiBoldText
            fontSize="16px"
            style={{ marginTop: "20px", marginBottom: "23px" }}
          >
            {t("language_screen.lanaguages_text")}
          </SemiBoldText>
          <InlineDiv className="inline-modal-div">
            {languages.current.map(
              (language: LanguageType) =>
                language.show && (
                  <LanguageContainer
                    key={language.id}
                    language={language}
                    languages={languages}
                    valuesState={valuesState}
                    deleteAll={deleteAll}
                  />
                ),
            )}
          </InlineDiv>
          <Separator
            style={{ background: PRIMARY_BUTTON_COLOR_DISSABLED, opacity: "1" }}
          />
        </section>
        <section className="mini-modal-section">
          <InlineDiv
            style={{ justifyContent: "space-between", marginTop: "20PX" }}
          >
            <BlackTextLink
              style={{
                color: NEUTRO_TITLES_COLOR,
                width: "fit-content",
                marginTop: "12px",
              }}
              fontSize="14px"
              onClick={() => {
                deleteAllFilters();
              }}
            >
              {t("activities.filter.clean")}
            </BlackTextLink>
            <CustomButton
              width="257px"
              height="50px"
              backgroundColor={PRIMARY_BUTTON_COLOR}
              onClick={() => {
                if (session) {
                  advancedFilters();
                }
                changeValuesForFetch();
                setOpen(false);
              }}
            >
              <RegularText fontSize="16px">
                {t("activities.filter.apply_filter")}
              </RegularText>
            </CustomButton>
          </InlineDiv>
        </section>
      </div>
    </Modal>
  );
};

export default ModalFilters;
